/**
 * `tour/tourSteps.ts` — the first-visit spotlight tour's six stops (#2048;
 * the sixth, Help, since 7 Oct 2026).
 *
 * Each stop points at a `data-tour="…"` anchor in the shell or on the team
 * home. A stop whose anchor is missing (or not on screen — the nav strip is
 * hidden on a phone, the panel toggle below `lg`) is skipped, so the tour
 * only ever spotlights something the lead can see.
 */

export interface TourStopTarget {
  /** The `data-tour` anchor this stop spotlights. */
  anchor: string;
  /** Three words at most. */
  title: string;
  /** One sentence. */
  body: string;
}

export interface TourStop {
  id: string;
  /** Tried in order; the first anchor found on screen wins. */
  targets: TourStopTarget[];
}

export const TOUR_STOPS: TourStop[] = [
  {
    id: "teams",
    targets: [
      {
        anchor: "team-switcher",
        title: "Your teams",
        body: "Switch between your teams here.",
      },
    ],
  },
  {
    id: "roster",
    targets: [
      {
        anchor: "nav-roster",
        title: "Roster",
        body: "The week, solved and published from here.",
      },
    ],
  },
  {
    id: "setup",
    targets: [
      {
        anchor: "nav-setup",
        title: "Setup",
        body: "Stations, people, rules and imports.",
      },
    ],
  },
  {
    id: "home",
    targets: [
      {
        anchor: "first-roster",
        title: "Your first roster",
        body: "Five steps, in order.",
      },
      {
        anchor: "nav-home",
        title: "Home",
        body: "What needs you this week.",
      },
    ],
  },
  {
    id: "next-steps",
    targets: [
      {
        anchor: "panel-toggle",
        title: "Next steps",
        body: "Your to-dos and what changed.",
      },
    ],
  },
  {
    id: "help",
    targets: [
      {
        anchor: "help",
        title: "Help",
        body: "The tour again, and guides to each part.",
      },
    ],
  },
];

export interface ResolvedTourStop extends TourStopTarget {
  id: string;
  element: HTMLElement;
}

/** Is the element laid out on screen (not `display: none`, not a zero box)? */
export function isAnchorShown(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/** The anchor's element, when it is in the document. */
export function findAnchor(anchor: string, root: ParentNode = document): HTMLElement | null {
  return root.querySelector<HTMLElement>(`[data-tour="${anchor}"]`);
}

/**
 * The stops that can show right now, in order: each stop's first target
 * whose anchor exists and is shown. Stops with no such target are skipped.
 */
export function resolveTourStops(
  root: ParentNode = document,
  isShown: (el: Element) => boolean = isAnchorShown,
  stops: TourStop[] = TOUR_STOPS,
): ResolvedTourStop[] {
  const out: ResolvedTourStop[] = [];
  for (const stop of stops) {
    for (const target of stop.targets) {
      const el = findAnchor(target.anchor, root);
      if (el && isShown(el)) {
        out.push({ ...target, id: stop.id, element: el });
        break;
      }
    }
  }
  return out;
}

/** `data-tour` value for a nav-strip link ("Roster" → "nav-roster"). */
export function navTourAnchor(label: string): string {
  return `nav-${label.toLowerCase().replace(/\s+/g, "-")}`;
}
